import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment, { type Moment } from "moment";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	isUsableDate,
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	useStraightenerKpisData,
	useStraightenerKpisPickers,
} from "../hooks/useStraightenerKpis";
import {
	ALL_PRODUCT_SIZE_ID,
	ALL_PRODUCT_SIZE_OPTION,
	CREW_OPTIONS,
	NO_PRODUCT_SIZE_OPTION,
	SHIFT_OPTIONS,
	type StraightenerKpiRow,
} from "../models/StraightenerKpis";
import { StraightenerKpisToolbar } from "./StraightenerKpisToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 28;
const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">No straightener runs were recorded in the selected range</span>`;

const DECIMAL_COLUMNS = new Set([
	"GapTimeMin",
	"GapTimeMax",
	"GapTimeAvg",
	"FPMSet",
	"FPMCalibrated",
]);

// The fact table stores dates, but the legacy branch of the proxy hands back a
// datetime shifted by the timezone conversion it still does. Cutting to ten
// characters shows the day each branch means without converting anything.
const asDate = (value: string | null | undefined): string =>
	value ? String(value).slice(0, 10) : "";

const asInteger = (value: number | null | undefined): string =>
	value == null ? "" : String(value);

// Two decimals and no thousands separator, which is what the legacy grid's
// toFixed(2) produces. That grid hides a real zero behind an empty cell; this
// one shows it.
const asNumber = (value: number | null | undefined): string =>
	value == null ? "" : value.toFixed(2);

// What each column actually is, in one line. Shown on the header and on every
// cell as a native title, so hovering answers "what is this number" without
// leaving the grid. Same pattern as the Product Changes report.
const DESCRIPTIONS: Record<string, string> = {
	ProductionDate: "Production date of the shift these runs belong to",
	ProductionShift: "D for the day shift, N for the night shift",
	CrewId: "Crew working that shift. Empty when none was on the roster",
	ProductSize:
		"Size running while the bars went through. Empty when it was not identified",
	Count: "Gap events in the group, one per bar leaving the straightener",
	GapTimeMin: "Shortest gap between two consecutive bars, in seconds",
	GapTimeMax:
		"Longest gap between two consecutive bars, in seconds. 300 is the ceiling the tracking records",
	GapTimeAvg: "Average gap between consecutive bars, in seconds",
	FPMSet: "Line speed the straightener was set to, in feet per minute",
	FPMCalibrated: "Line speed actually measured, in feet per minute",
};

// Column order follows the legacy grid, which is not the order the procedure
// returns: the gap times come before the two speeds there.
const RAW_COLUMN_DEFS: ColDef<StraightenerKpiRow>[] = [
	{
		field: "ProductionDate",
		headerName: "Prod Date",
		flex: 12,
		minWidth: 110,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asDate(p.value),
	},
	{
		field: "ProductionShift",
		headerName: "Shift",
		flex: 7,
		minWidth: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CrewId",
		headerName: "Crew",
		flex: 7,
		minWidth: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductSize",
		headerName: "Product Size",
		flex: 18,
		minWidth: 150,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Count",
		headerName: "Count",
		flex: 9,
		minWidth: 85,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asInteger(p.value),
	},
	{
		field: "GapTimeMin",
		headerName: "Gap Time Min",
		flex: 12,
		minWidth: 115,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "GapTimeMax",
		headerName: "Gap Time Max",
		flex: 12,
		minWidth: 115,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "GapTimeAvg",
		headerName: "Gap Time Avg",
		flex: 12,
		minWidth: 115,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "FPMSet",
		headerName: "FPM Set",
		flex: 10,
		minWidth: 95,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "FPMCalibrated",
		headerName: "FPM Calibrated",
		flex: 13,
		minWidth: 125,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
];

const withTooltips = (
	defs: ColDef<StraightenerKpiRow>[],
): ColDef<StraightenerKpiRow>[] =>
	defs.map((def) => {
		const text = def.field ? DESCRIPTIONS[def.field] : undefined;
		if (!text) return def;
		return { ...def, headerTooltip: text, tooltipValueGetter: () => text };
	});

const COLUMN_DEFS = withTooltips(RAW_COLUMN_DEFS);

// The legacy screen opens on the last three days: today minus two, to today.
const defaultStart = () => moment().startOf("day").subtract(2, "day");
const defaultEnd = () => moment().startOf("day");

// The product size carries two sentinels: -2 is the "All" option, which the procedure
// gets as null and never reaches the URL, and -1 is "None", a real value that does.
const URL_SCHEMA = {
	start: urlParam.date(defaultStart),
	end: urlParam.date(defaultEnd),
	productSizeId: urlParam.int(),
	productionShift: urlParam.oneOf(SHIFT_OPTIONS),
	crewId: urlParam.oneOf(CREW_OPTIONS),
};

export const StraightenerKpisReport = () => {
	const [
		{ start, end, productSizeId, productionShift, crewId },
		setFilters,
		{ resetValues, dropValues },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<StraightenerKpiRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useStraightenerKpisData({
		start: startStr,
		end: endStr,
		productSizeId,
		crewId,
		productionShift,
	});
	const pickersQuery = useStraightenerKpisPickers();

	const rows = dataQuery.data ?? [];
	// All, None, then the catalog, which is the order the legacy dropdown reads.
	const productSizes = useMemo(
		() => [
			ALL_PRODUCT_SIZE_OPTION,
			NO_PRODUCT_SIZE_OPTION,
			...(pickersQuery.data ?? []),
		],
		[pickersQuery.data],
	);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<StraightenerKpiRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);

	const handleStartChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ start: d });
		},
		[setFilters],
	);

	const handleEndChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ end: d });
		},
		[setFilters],
	);

	// A shared link can name a product size that is no longer in the catalog. The two
	// sentinels are always valid.
	useDropUnknownValue(
		productSizeId,
		pickersQuery.isSuccess,
		useMemo(() => productSizes.map((size) => size.Id), [productSizes]),
		useCallback(() => dropValues("productSizeId"), [dropValues]),
	);

	const handleReset = useCallback(() => {
		resetValues();
		dataQuery.refetch();
	}, [dataQuery, resetValues]);

	// The legacy workbook writes GapTimeAvg into the same column as GapTimeMax and
	// loses one of them. This one writes all ten, with the decimals the grid shows.
	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "StraightenerKPIs.xlsx",
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "ProductionDate") return asDate(params.value);
				if (DECIMAL_COLUMNS.has(colId))
					return params.value == null ? "" : Number(params.value.toFixed(2));
				return params.value ?? "";
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Straightener KPIs
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<StraightenerKpisToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					productSizeId={productSizeId}
					onProductSizeChange={(id) =>
						setFilters({
							productSizeId: id === ALL_PRODUCT_SIZE_ID ? null : id,
						})
					}
					productSizes={productSizes}
					productionShift={productionShift}
					onProductionShiftChange={(value) =>
						setFilters({
							productionShift: value as (typeof SHIFT_OPTIONS)[number] | null,
						})
					}
					crewId={crewId}
					onCrewChange={(value) =>
						setFilters({
							crewId: value as (typeof CREW_OPTIONS)[number] | null,
						})
					}
					onReset={handleReset}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
				{dataQuery.isInitialLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ height: "100%" }}>
						<AgGridReact<StraightenerKpiRow>
							columnDefs={COLUMN_DEFS}
							rowData={rows}
							rowModelType="clientSide"
							pagination={false}
							defaultColDef={{ resizable: true, sortable: true }}
							suppressContextMenu
							enableBrowserTooltips
							// Without the pair, the vertical scrollbar appearing on a long
							// result narrows the body after the flex widths are set and the
							// last column, FPM Calibrated, stops rendering.
							alwaysShowVerticalScroll
							suppressColumnVirtualisation
							overlayNoRowsTemplate={NO_ROWS_OVERLAY}
							headerHeight={HEADER_HEIGHT}
							rowHeight={ROW_HEIGHT}
							onGridReady={handleGridReady}
						/>
					</Box>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
